// // apiClient.js
// import axios from 'axios';
// import { server_api } from '../utils/constants';

// const apiClient = axios.create({
//   baseURL: server_api(),  // The base URL for your API
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

// // Interceptor to add the auth token to requests
// apiClient.interceptors.request.use((config) => {
//   const token = localStorage.getItem('authToken');
//   if (token) {
//     // config.headers.Authorization = `Bearer ${token}`;
//   }
//   return config;
// });

// export default apiClient;


// apiClient.js
import axios from 'axios';
import { server_api } from '../utils/constants';

const apiClient = axios.create({
  baseURL: server_api(),  // The base URL for your API
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to add the auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  
  // Skip token check for public endpoints
  const publicEndpoints = [
    '/api/auth/login/',
    '/api/auth/refresh-token/',
    '/api/auth/signup/',
    '/api/auth/forgot-password/',
    '/api/auth/reset-password/'
  ];
  const isPublicEndpoint = publicEndpoints.some(endpoint => config.url.includes(endpoint));
  
  if (!isPublicEndpoint) {
    // For protected endpoints, check if token exists
    if (!token && !localStorage.getItem('refreshToken')) {
      // No token available, redirect to login
      handleLogout();
      return Promise.reject(new Error('No authentication token available'));
    }
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


// Flag to prevent infinite refresh loops
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Custom event for subscription issues
const SUBSCRIPTION_EVENT = 'subscription-access-denied';

// Function to dispatch subscription event
function dispatchSubscriptionEvent(data) {
  const event = new CustomEvent(SUBSCRIPTION_EVENT, { detail: data });
  window.dispatchEvent(event);
}

// Export for components to listen to subscription events
export const subscriptionEventName = SUBSCRIPTION_EVENT;

// 🔹 Response interceptor for handling token expiration and refresh
apiClient.interceptors.response.use(
  (response) => {
    // Handle successful responses
    if (response.data?.message?.toLowerCase() === 'expired') {
      handleLogout();
      return Promise.reject({ message: 'expired' });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle subscription-related 403 errors from backend middleware
    if (error.response?.status === 403) {
      const responseData = error.response.data;
      const subscriptionStatus = responseData?.status;

      // Check if this is a subscription-related error
      if (['pending', 'expired', 'cancelled', 'no_subscription'].includes(subscriptionStatus)) {
        // Dispatch event for subscription issues
        dispatchSubscriptionEvent({
          status: subscriptionStatus,
          message: responseData?.message,
          subscriptionId: responseData?.subscription_id,
          expiredOn: responseData?.expired_on
        });

        // Return the error with subscription details
        return Promise.reject({
          ...error,
          isSubscriptionError: true,
          subscriptionStatus: subscriptionStatus,
          subscriptionMessage: responseData?.message
        });
      }
    }

    // Check if error is 401 (Unauthorized) and not a refresh token request or login request
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/api/auth/refresh-token/') && !originalRequest.url.includes('/api/auth/login/')) {
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Attempt to refresh the token
        const response = await axios.post(`${server_api()}/api/auth/refresh-token/`, {
          refresh_token: refreshToken
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          const { access, refresh, user } = response.data.data;
          
          // Update tokens in localStorage
          localStorage.setItem('authToken', access);
          localStorage.setItem('refreshToken', refresh);
          
          // Update user data if provided
          if (user) {
            localStorage.setItem('UserRole', user.role);
            localStorage.setItem('UserMobile', user.mobile);
            localStorage.setItem('UserId', user.id);
            localStorage.setItem('user', JSON.stringify(user));
          }

          // Update the authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${access}`;
          
          // Process any queued requests
          processQueue(null, access);
          
          // Retry the original request with new token
          return apiClient(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    const errorMessage = error.response?.data?.message || error.message || 'Unexpected error';
    if (errorMessage.toLowerCase() === 'expired') {
      handleLogout();
    }
    
    return Promise.reject(error);
  }
);

function handleLogout() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('UserRole');
  localStorage.removeItem('UserName');
  localStorage.removeItem('UserMobile');
  localStorage.removeItem('UserId');
  localStorage.removeItem('dark-mode');
  localStorage.removeItem('user');

  window.location.href = '/';   // Redirect to login page
}

export default apiClient;
